<template>
  <div :class="sidebarClasses" class="d-flex flex-column" style="width: 240px;">
    <div class="sidebar-logo text-center mb-4">
      <img src="/logo_b.png" alt="SICECOM" class="client-sidebar-logo" />
    </div>
    
    <div v-if="loadingModules" class="text-center py-4 flex-grow-1">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>
    
    <ul v-else class="nav flex-column flex-nowrap flex-grow-1 scrollable-menu w-100">
      <li v-for="(section, idx) in menuSections" :key="section.code" class="nav-item mb-2 w-100">
        <template v-if="section.to">
          <router-link :to="section.to" class="nav-link d-flex align-items-center w-100">
            <span class="d-flex align-items-center text-truncate me-2">
              <i :class="getModuleIcon(section.code)" class="me-2"></i>
              {{ section.label }}
            </span>
          </router-link>
        </template>
        <template v-else>
          <div 
            class="nav-link d-flex justify-content-between align-items-center w-100" 
            style="cursor:pointer" 
            @click="toggleMenu(idx)" 
            :aria-expanded="isOpen(idx)" 
            tabindex="0" 
            @keydown.enter.prevent="toggleMenu(idx)"
          >
            <span class="d-flex align-items-center text-truncate me-2">
              <i :class="getModuleIcon(section.code)" class="me-2"></i>
              {{ section.label }}
            </span>
            <span v-if="section.children && section.children.length > 0" class="toggle-icon flex-shrink-0">
              <i :class="isOpen(idx) ? 'bi bi-dash-circle' : 'bi bi-plus-circle'"></i>
            </span>
          </div>
        </template>
        <ul v-show="isOpen(idx)" class="nav flex-column flex-nowrap ms-3 mt-1 w-100">
            <li v-for="item in section.children" :key="item.code" class="nav-item mb-1 w-100">
              <!-- Sub-grupo colapsable (nivel 3) -->
              <template v-if="item.children && item.children.length > 0">
                <div
                  class="nav-link submenu-link submenu-group-header d-flex justify-content-between align-items-center w-100"
                  style="cursor:pointer"
                  @click="toggleSubmenu(item.code)"
                >
                  <span class="d-flex align-items-center text-truncate me-1">
                    <i class="bi bi-folder2 me-1"></i>{{ item.label }}
                  </span>
                  <i :class="isSubmenuOpen(item.code) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="flex-shrink-0"></i>
                </div>
                <ul v-show="isSubmenuOpen(item.code)" class="nav flex-column flex-nowrap ms-3 mt-1 w-100">
                    <li v-for="subitem in item.children" :key="subitem.code" class="nav-item mb-1 w-100">
                      <router-link
                        v-if="subitem.to"
                        :to="subitem.to"
                        class="nav-link submenu-link submenu-level-3 d-block text-truncate"
                      >
                        <i class="bi bi-dash me-1"></i>{{ subitem.label }}
                      </router-link>
                    </li>
                  </ul>
              </template>
              <!-- Item directo (nivel 2) -->
              <template v-else>
                <router-link v-if="item.to" :to="item.to" class="nav-link submenu-link d-block text-truncate">
                  {{ item.label }}
                </router-link>
                <span v-else class="nav-link submenu-link d-block text-truncate">{{ item.label }}</span>
              </template>
            </li>
          </ul>
      </li>
    </ul>

    <div class="mt-auto pt-3 border-top">
      <ul class="nav flex-column">
        <li class="nav-item">
          <a href="#" @click.prevent="handleLogout" class="nav-link text-danger">
            <i class="bi bi-box-arrow-right me-2"></i>Cerrar sesión
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { cerrarSesion } from '@/services/servicioAutenticacion'
import themeManager from '@/store/theme'

export default {
  name: 'ClientSidebar',
  data() {
    return {
      openMenus: [],
      activeModules: [],
      loadingModules: true,
      openSubmenus: []
    }
  },
  mounted() {
    this.loadActiveModules()
  },
  computed: {
    sidebarClasses() {
      return `vh-100 p-3 ${themeManager.themeClass}`;
    },
    rutasPermitidas() {
      const mods = this.$store.getters.activeModules || []
      return new Set(mods.map(String))
    },
    suscripcionBloqueada() {
      const e = this.$store.getters.subscriptionEstado
      return e === 'vencida' || e === 'cancelada'
    },
    menuSections() {
      if (this.loadingModules || !this.$store.state.modulesLoaded) return []
      // Vencida o cancelada: solo gestion de pago
      if (this.suscripcionBloqueada) {
        return [
          { code: 'pago', label: 'Gestion de pago', to: '/gestion-pago', children: [] }
        ]
      }
      const base = this.getDefaultMenuSections()
      // Si ya cargaron los permisos y el set queda vacio, no hay modulos
      // habilitados para este usuario: no se debe mostrar el menu completo.
      return this.filtrarSecciones(base)
    }
  },
  
  methods: {
    filtrarSecciones(secciones) {
      const permitidas = this.rutasPermitidas
      // permCheck permite que un item de menu navegue a una ruta propia
      // (to) pero se muestre/oculte segun el permiso de otra ruta ya
      // existente en el catalogo de submodulos (ej: Kardex reusa el
      // permiso de Stock, ya que no tiene submodulo propio).
      const visible = (item) => {
        const clave = item.permCheck || item.to
        return !!clave && permitidas.has(clave)
      }

      const result = []
      for (const sec of secciones) {
        // Caso 1: seccion con link directo (sin children)
        if (sec.to && (!sec.children || !sec.children.length)) {
          if (visible(sec)) result.push(sec)
          continue
        }
        // Caso 2: seccion con children (puede haber sub-grupos nivel 3)
        const childrenFiltrados = []
        for (const item of (sec.children || [])) {
          if (item.children && item.children.length) {
            const subFiltrados = item.children.filter(s => visible(s))
            if (subFiltrados.length) childrenFiltrados.push({ ...item, children: subFiltrados })
          } else if (visible(item)) {
            childrenFiltrados.push(item)
          }
        }
        if (childrenFiltrados.length) {
          result.push({ ...sec, children: childrenFiltrados })
        }
      }
      return result
    },

    getDefaultMenuSections() {
      return [
        // 1. Inicio
        { code: '1', label: 'Inicio', to: '/dashboard', children: [] },
        // 2. Facturación
        {
          code: '2',
          label: 'Facturación',
          children: [
            { code: '2.1', label: 'Nueva factura',       to: '/ventas/facturacion' },
            { code: '2.2', label: 'Documentos emitidos', to: '/ventas/documentos-emitidos' },
            { code: '2.3', label: 'Notas de crédito',    to: '/ventas/notas-credito' },
            { code: '2.4', label: 'Guías de remisión',   to: '/ventas/guias-remision' },
            { code: '2.5', label: 'Nota de venta',       to: '/ventas/nota-venta' }
          ]
        },
        // 3. Pedidos
        {
          code: '3',
          label: 'Pedidos',
          children: [
            { code: '3.1', label: 'Listado de pedidos', to: '/ventas/pedidos' },
            { code: '3.2', label: 'Nuevo pedido',        to: '/ventas/pedidos/nuevo' }
          ]
        },
        // 3b. Taller
        {
          code: '3b',
          label: 'Taller',
          children: [
            { code: '3b.1', label: 'Ordenes de taller', to: '/taller/ordenes' },
            { code: '3b.2', label: 'Nueva orden',        to: '/taller/ordenes/nuevo' }
          ]
        },
        // 4. Proformas
        {
          code: '4',
          label: 'Proformas',
          children: [
            { code: '4.1', label: 'Listado',        to: '/proformas' },
            { code: '4.2', label: 'Nueva proforma', to: '/proformas/nueva' }
          ]
        },
        // 5. Clientes
        {
          code: '5',
          label: 'Clientes',
          children: [
            { code: '5.1', label: 'Clientes',        to: '/clientes' },
            { code: '5.2', label: 'Estado de cuenta', to: '/clientes/estado-cuenta' },
            { code: '5.3', label: 'Carga masiva',    to: '/clientes/carga-masiva' }
          ]
        },
        // 6. Productos
        {
          code: '6',
          label: 'Productos',
          children: [
            { code: '6.1', label: 'Productos',    to: '/inventario/productos' },
            { code: '6.2', label: 'Categorías',   to: '/inventario/categorias' },
            { code: '6.3', label: 'Marcas',       to: '/inventario/marcas' },
            { code: '6.4', label: 'Unidades',     to: '/inventario/unidades-medida' },
            { code: '6.5', label: 'Almacenes',    to: '/inventario/almacenes' },
            { code: '6.6', label: 'Stock',        to: '/inventario/stock' },
            { code: '6.7', label: 'Kardex de productos', to: '/inventario/movimientos', permCheck: '/inventario/stock' },
            { code: '6.8', label: 'Carga masiva', to: '/inventario/carga-masiva' }
          ]
        },
        // 7. Compras
        {
          code: '7',
          label: 'Compras',
          children: [
            { code: '7.1', label: 'Mis compras',         to: '/compras/ordenes' },
            { code: '7.2', label: 'Registrar compra',    to: '/compras/registrar' },
            { code: '7.3', label: 'Proveedores',         to: '/personas/proveedores' },
            { code: '7.4', label: 'Carga masiva prov.',  to: '/personas/proveedores/carga-masiva' },
            { code: '7.5', label: 'Estado de cuenta',    to: '/compras/estado-cuenta' }
          ]
        },
        // 8. Caja
        {
          code: '8',
          label: 'Caja',
          children: [
            { code: '8.1', label: 'Control de Caja',      to: '/cajas' },
            { code: '8.2', label: 'Historial sesiones',   to: '/cajas/historial' },
            { code: '8.3', label: 'Configurar cajas',     to: '/cajas/configuracion' },
            { code: '8.4', label: 'Bancos y movimientos', to: '/finanzas/bancos' }
          ]
        },
        // 9. Reportes
        {
          code: '9',
          label: 'Reportes',
          children: [
            { code: '9.1', label: 'Ventas',                to: '/reportes/ventas' },
            { code: '9.2', label: 'Utilidad aproximada',   to: '/reportes/utilidad-aproximada' },
            { code: '9.3', label: 'Productos vendidos',    to: '/reportes/productos-vendidos' },
            { code: '9.4', label: 'Clientes con deuda',    to: '/reportes/clientes-deuda' },
            { code: '9.5', label: 'Proveedores con deuda', to: '/reportes/proveedores-deuda' },
            { code: '9.6', label: 'ATS',                   to: '/reportes/ats' },
            { code: '9.7', label: 'Comisiones',            to: '/reportes/comisiones' },
            { code: '9.8', label: 'Auditoría',              to: '/reportes/auditoria' }
          ]
        },
        // 10. Configuración
        {
          code: '10',
          label: 'Configuración',
          children: [
            { code: '10.1', label: 'SRI / Facturación', to: '/configuracion/sri' },
            { code: '10.2', label: 'Secuenciales',      to: '/configuracion/secuenciales' },
            { code: '10.3', label: 'Usuarios',          to: '/configuracion/usuarios' },
            { code: '10.4', label: 'Correos',           to: '/configuracion/correos' },
            { code: '10.5', label: 'Plantillas',        to: '/configuracion/plantillas' },
            { code: '10.6', label: 'Impresiones',       to: '/configuracion/impresiones' },
            { code: '10.7', label: 'Comisiones',        to: '/configuracion/comisiones' },
            { code: '10.8', label: 'Roles y permisos',  to: '/control-acceso/roles' }
          ]
        }
      ]
    },

    async loadActiveModules() {
      // El filtrado real se hace en el computed menuSections via store.
      // Marcamos loadingModules en false una vez el store ya tiene datos
      // (o los carga el ClientLayout); aqui solo desactivamos el spinner.
      this.activeModules = []
      this.loadingModules = false
    },

    getModuleIcon(code) {
      // Mapeo de iconos por código de módulo principal
      const iconMap = {
        '1':  'bi bi-house-door',
        '2':  'bi bi-receipt',
        '3':  'bi bi-bag-check',
        '4':  'bi bi-file-earmark-text',
        '5':  'bi bi-people',
        '6':  'bi bi-box-seam',
        '7':  'bi bi-bag',
        '8':  'bi bi-cash-stack',
        '9':  'bi bi-bar-chart',
        '10': 'bi bi-gear',
        'pago': 'bi bi-credit-card-2-front'
      }
      
      return iconMap[code] || 'bi bi-circle'
    },

    toggleMenu(menuIndex) {
      if (this.openMenus.includes(menuIndex)) {
        this.openMenus = this.openMenus.filter(idx => idx !== menuIndex)
      } else {
        this.openMenus = [menuIndex]
      }
    },

    isOpen(menuName) {
      return this.openMenus.includes(menuName)
    },

    toggleSubmenu(codigo) {
      if (this.openSubmenus.includes(codigo)) {
        this.openSubmenus = this.openSubmenus.filter(c => c !== codigo)
      } else {
        this.openSubmenus = [...this.openSubmenus, codigo]
      }
    },

    isSubmenuOpen(codigo) {
      return this.openSubmenus.includes(codigo)
    },

    handleLogout() {
      cerrarSesion()
      this.$router.push('/iniciar-sesion')
    }
  }
}
</script>

<style scoped>


.toggle-icon {
  font-weight: bold;
  font-size: 1.1em;
}

.toggle-icon i {
  color: var(--primary-main) !important;
}

/* Iconos del menú principal */
.nav-link > span > i:first-child {
  font-size: 1.1em;
  width: 20px;
  text-align: center;
  color: var(--primary-main);
}

.submenu-link {
  padding-left: 1rem;
  font-size: 0.9em;
  color: var(--text-secondary) !important;
}

.submenu-link:hover {
  color: var(--primary-main) !important;
}

.nav-link {
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.nav-link:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.scrollable-menu {
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Crucial para que el scroll funcione dentro de flexbox */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

/* Webkit (Chrome, Safari, Edge) scrollbar styling */
.scrollable-menu::-webkit-scrollbar {
  width: 6px;
}

.scrollable-menu::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-menu::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.scrollable-menu::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema claro - texto negro en el sidebar */
.light-theme .nav-link {
  color: #212529 !important;
}

.light-theme .nav-link:hover {
  color: #000 !important;
}

.light-theme .submenu-link {
  color: #495057 !important;
}

.light-theme .submenu-link:hover {
  color: #0d6efd !important;
}

/* Tema oscuro - texto claro en el sidebar */
.dark-theme .nav-link {
  color: #e9ecef !important;
}

.dark-theme .nav-link:hover {
  color: #fff !important;
}

.dark-theme .submenu-link {
  color: #adb5bd !important;
}

.dark-theme .submenu-link:hover {
  color: #0d6efd !important;
}
</style>





